/**
 * Hello Retail Recoms
 *
 * Powers <section> instances of helloretail-recoms.liquid.
 *
 * Each section element carries its configuration on data-* attributes:
 *   data-hr-recom-section, data-section-id, data-recom-key,
 *   data-stack-mobile, data-stack-desktop, data-products-limit-mobile,
 *   data-hide-info, data-reveal
 *
 * Network behavior: every recoms section on the page is bundled into
 * a single hrq.push(['loadRecom', [...requests], cb]) call. Hello Retail's
 * loadRecom accepts an array of RecomRequest objects and returns
 * result.responses[i] in the same order. As a bonus, products are
 * deduplicated across responses within one batch — the same product
 * will never appear in two boxes simultaneously.
 * See https://developer.helloretail.com/sdk/recoms/javascript_sdk
 *
 * Cards are rendered by cloning hr-product-card-tpl (defined in
 * snippets/hello-retail-templates.liquid) and filling slots via
 * HelloRetailHelpers.CARD_SLOT_FILLERS — the same path used by
 * predictive search and full search.
 *
 * Recoms-specific behavior preserved here:
 *   - Wrap the cloned card root in <product-card> so the parent
 *     <product-list>'s reveal-on-scroll animation picks it up.
 *   - Optional reveal-on-scroll attribute (theme setting).
 *   - Optional removal of .product-card__info (section setting).
 *   - Uniquify carousel slide IDs across (sectionId × grid|carousel)
 *     so duplicates rendered for the hidden grid/carousel pair never collide.
 *   - Replicate scroll-carousel edge events for a carousel that starts empty.
 *
 * Depends on: hello-retail-helpers.js + snippets/hello-retail-templates.liquid
 */
(function () {
  'use strict';

  if (window.__hrRecomsLoaded) return;
  window.__hrRecomsLoaded = true;

  // ─── Init ─────────────────────────────────────────────────────────────────

  /**
   * Collect every recoms section's request and fire a single batched
   * loadRecom. Each registration captures its own (cfg, DOM refs) via
   * closure so the response handler can render to the right section.
   */
  function init() {
    var sections = document.querySelectorAll('[data-hr-recom-section]');
    if (!sections.length) return;

    var registrations = [];
    var pageUrl       = window.location.origin + window.location.pathname;

    sections.forEach(function (sectionEl) {
      var cfg = readConfig(sectionEl);

      if (!cfg.recomKey) {
        console.warn('[HR Recom] no recom_key set in section settings — aborting', sectionEl);
        return;
      }

      var listGrid     = document.getElementById('hr-recom-list-' + cfg.sectionId);
      var listCarousel = document.getElementById('carousel-' + cfg.sectionId);

      if (!listGrid || !listCarousel) {
        console.warn('[HR Recom] missing DOM elements for section', cfg.sectionId);
        return;
      }

      registrations.push({
        request: {
          key: cfg.recomKey,
          format: 'json',
          context: { urls: [pageUrl] }
        },
        handle: function (recom) {
          var products = recom && recom.products;
          if (!products || !products.length) return;
          render(cfg, sectionEl, listGrid, listCarousel, products);
        }
      });
    });

    if (!registrations.length) return;

    window.hrq = window.hrq || [];
    window.hrq.push(['loadRecom',
      registrations.map(function (r) { return r.request; }),
      function (result) {
        var responses = normalizeRecomResult(result);
        if (!responses) return;
        registrations.forEach(function (reg, i) {
          reg.handle(responses[i]);
        });
      }
    ]);
  }

  /**
   * Hello Retail's `loadRecom` returns either:
   *   - { success, message, responses: [...] }  (current SDK shape)
   *   - [...]                                   (legacy bare array)
   * Returns null on a failed batch (logged) or on an unknown shape.
   */
  function normalizeRecomResult(result) {
    if (Array.isArray(result)) return result;
    if (result && Array.isArray(result.responses)) {
      if (result.success === false) {
        console.error('[HR Recom] loadRecom failed:', result.message);
        return null;
      }
      return result.responses;
    }
    console.warn('[HR Recom] unexpected loadRecom result shape', result);
    return null;
  }

  function readConfig(el) {
    var ds = el.dataset;
    return {
      sectionId:           ds.sectionId || '',
      recomKey:            ds.recomKey || '',
      stackMobile:         ds.stackMobile === 'true',
      stackDesktop:        ds.stackDesktop === 'true',
      productsLimitMobile: parseInt(ds.productsLimitMobile, 10) || 0,
      hideInfo:            ds.hideInfo === 'true',
      reveal:              ds.reveal === 'true'
    };
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  function render(cfg, sectionEl, listGrid, listCarousel, products) {
    var isMobile = window.matchMedia('(max-width: 699px)').matches;
    var visible  = (isMobile && cfg.productsLimitMobile)
      ? products.slice(0, cfg.productsLimitMobile)
      : products;

    listGrid.innerHTML     = '';
    listCarousel.innerHTML = '';

    var gridFrag     = document.createDocumentFragment();
    var carouselFrag = document.createDocumentFragment();

    visible.forEach(function (p) {
      gridFrag.appendChild(buildCard(p, cfg, '-g'));
      carouselFrag.appendChild(buildCard(p, cfg, '-c'));
    });

    listGrid.appendChild(gridFrag);
    listCarousel.appendChild(carouselFrag);

    sectionEl.style.display = '';
    attachCarouselEdgeEvents(listCarousel);
  }

  /**
   * Clone hr-product-card-tpl, fill slots, wrap in <product-card>.
   * The wrapper is required because <product-list>'s reveal-on-scroll
   * logic queries `product-card[reveal-on-scroll="true"]`.
   */
  function buildCard(p, cfg, listSuffix) {
    var H    = window.HelloRetailHelpers;
    var frag = H.cloneTemplate('hr-product-card-tpl');
    if (!frag) return document.createDocumentFragment();

    H.fillSlots(frag, H.CARD_SLOT_FILLERS, p);

    var innerEl = frag.querySelector('.product-card');
    if (!innerEl) return frag;

    uniquifySlideIds(frag, '-' + cfg.sectionId + listSuffix);

    var wrapper = document.createElement('product-card');
    wrapper.className = innerEl.className;
    if (cfg.reveal) wrapper.setAttribute('reveal-on-scroll', 'true');

    while (innerEl.firstChild) wrapper.appendChild(innerEl.firstChild);
    innerEl.replaceWith(wrapper);

    if (cfg.hideInfo) {
      var info = wrapper.querySelector('.product-card__info');
      if (info) info.remove();
    }

    return frag;
  }

  /**
   * The same product can render in both the (hidden) grid and the
   * (hidden) carousel for one section, and across multiple sections on
   * one page. Suffix each carousel slide id + control data-slide-id so
   * the in-card carousel navigation never collides.
   */
  function uniquifySlideIds(fragment, suffix) {
    fragment.querySelectorAll('.carousel__slide').forEach(function (slide) {
      if (slide.id) slide.id = slide.id + suffix;
    });
    fragment.querySelectorAll('.carousel__control').forEach(function (btn) {
      var sid = btn.getAttribute('data-slide-id');
      if (sid) btn.setAttribute('data-slide-id', sid + suffix);
    });
  }

  /**
   * scroll-carousel attaches its scroll listener in the constructor only
   * when allCells.length > 1 at that moment. Since our carousels start
   * empty (products injected after page load), that check fails and
   * scroll:edge-leaving never fires — leaving the prev button permanently
   * disabled. Replicate the edge-event logic so the buttons behave
   * correctly. Matches the previous inline implementation.
   */
  function attachCarouselEdgeEvents(el) {
    var s = { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true };
    el.addEventListener('scroll', function () {
      var threshold  = 100;
      var scrollLeft = Math.round(Math.abs(el.scrollLeft));

      if (scrollLeft < threshold && s.nearingStart) {
        el.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'start' } }));
        s.nearingStart = false; s.leavingStart = true;
      }
      if (scrollLeft >= threshold && s.leavingStart) {
        el.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'start' } }));
        s.leavingStart = false; s.nearingStart = true;
      }
      if (el.scrollWidth - el.clientWidth < scrollLeft + threshold && s.nearingEnd) {
        el.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'end' } }));
        s.nearingEnd = false; s.leavingEnd = true;
      }
      if (el.scrollWidth - el.clientWidth >= scrollLeft + threshold && s.leavingEnd) {
        el.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'end' } }));
        s.leavingEnd = false; s.nearingEnd = true;
      }
    });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────

  function boot() {
    if (window.HelloRetailHelpers) {
      init();
    } else {
      document.addEventListener('hr:helpers:ready', init);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
